import type { ZodTypeAny, z } from 'zod';
import { ApiError } from './errors';
import { env } from '@config/env';

/**
 * Adapter the auth layer registers so the client can read the current
 * access token and request a silent refresh on 401. We keep this as a
 * registration pattern (vs direct import) to avoid a circular dep
 * between the client and the auth provider.
 */
export interface AuthAdapter {
  getToken(): string | null;
  refresh(): Promise<string | null>;
  onUnauthorized?: () => void;
}

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions<TSchema extends ZodTypeAny> {
  readonly path: string;
  readonly schema: TSchema;
  readonly method?: HttpMethod;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  readonly timeoutMs?: number;
  readonly skipAuth?: boolean;
}

const DEFAULT_TIMEOUT_MS = 15_000;

export class ApiClient {
  private adapter: AuthAdapter | null = null;
  private readonly baseUrl: string;

  constructor(opts: { readonly baseUrl: string }) {
    this.baseUrl = opts.baseUrl.endsWith('/') ? opts.baseUrl.slice(0, -1) : opts.baseUrl;
  }

  setAuthAdapter(adapter: AuthAdapter): void {
    this.adapter = adapter;
  }

  async request<TSchema extends ZodTypeAny>(opts: RequestOptions<TSchema>): Promise<z.infer<TSchema>> {
    const method = opts.method ?? 'GET';
    const url = this.buildUrl(opts.path, opts.query);
    const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;

    const exec = async (token: string | null): Promise<Response> => {
      const headers = new Headers({ accept: 'application/json' });
      if (opts.body !== undefined) headers.set('content-type', 'application/json');
      if (!opts.skipAuth) this.applyAuthHeaders(headers, token);

      const init: RequestInit = {
        method,
        headers,
        credentials: 'include',
        signal: this.combineSignal(opts.signal, timeoutMs),
      };
      if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

      try {
        return await fetch(url, init);
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          throw ApiError.timeout(timeoutMs);
        }
        throw ApiError.network(e);
      }
    };

    let res = await exec(this.adapter?.getToken() ?? null);

    // 401 → attempt silent refresh exactly once, then retry.
    if (res.status === 401 && !opts.skipAuth && this.adapter) {
      const refreshed = await this.adapter.refresh();
      if (refreshed) {
        res = await exec(refreshed);
      } else {
        this.adapter.onUnauthorized?.();
      }
    }

    if (!res.ok) {
      const body = await this.safeJson(res);
      throw ApiError.fromResponse(res, body);
    }

    if (res.status === 204) {
      // No content — schema must allow void/undefined or unit.
      const parsed = opts.schema.safeParse(undefined);
      if (!parsed.success) throw ApiError.contract(parsed.error.issues);
      // Zod's `safeParse` returns `any` on a generic schema; the runtime guard
      // above is what enforces the type, so the cast is safe.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return parsed.data as z.infer<TSchema>;
    }

    const json = await this.safeJson(res);
    const parsed = opts.schema.safeParse(this.unwrapEnvelope(json));
    if (!parsed.success) {
      throw ApiError.contract(parsed.error.issues);
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return parsed.data as z.infer<TSchema>;
  }

  /**
   * Authenticated file download (CSV/PDF/etc.) — fetches with the Bearer token
   * and triggers a browser save. Not JSON, so it sits outside `request()`.
   */
  async download(path: string, filename: string): Promise<void> {
    const url = this.buildUrl(path);
    const headers = new Headers({ accept: '*/*' });
    this.applyAuthHeaders(headers, this.adapter?.getToken() ?? null);

    const res = await fetch(url, { headers, credentials: 'include' });
    if (!res.ok) {
      throw ApiError.fromResponse(res, await this.safeJson(res));
    }

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  }

  /**
   * POST and consume a Server-Sent Events stream, invoking `onEvent` for each
   * parsed `data: {json}` payload. Used by the docs assistant for token-by-token
   * answers. Sits outside `request()` because the response isn't a JSON envelope.
   */
  async stream(
    path: string,
    body: unknown,
    onEvent: (event: Record<string, unknown>) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const url = this.buildUrl(path);
    const headers = new Headers({ accept: 'text/event-stream', 'content-type': 'application/json' });
    this.applyAuthHeaders(headers, this.adapter?.getToken() ?? null);

    const init: RequestInit = { method: 'POST', headers, body: JSON.stringify(body) };
    if (signal !== undefined) init.signal = signal; // exactOptionalPropertyTypes: don't pass signal:undefined
    const res = await fetch(url, init);
    if (!res.ok || res.body === null) {
      throw ApiError.fromResponse(res, await this.safeJson(res));
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let nl = buf.indexOf('\n');
      while (nl !== -1) {
        const line = buf.slice(0, nl).trim();
        buf = buf.slice(nl + 1);
        if (line.startsWith('data:')) {
          const data = line.slice(5).trim();
          let parsed: Record<string, unknown> | null = null;
          try {
            parsed = data === '' ? null : (JSON.parse(data) as Record<string, unknown>);
          } catch {
            parsed = null;
          }
          if (parsed !== null) onEvent(parsed);
        }
        nl = buf.indexOf('\n');
      }
    }
  }

  /** Set the Bearer token. No-op without a token, so unauthenticated requests stay header-free. */
  private applyAuthHeaders(headers: Headers, token: string | null): void {
    if (!token) return;
    headers.set('authorization', `Bearer ${token}`);
  }

  private buildUrl(
    path: string,
    query?: Readonly<Record<string, string | number | boolean | undefined>>,
  ): string {
    const url = new URL(path.startsWith('/') ? path.slice(1) : path, `${this.baseUrl}/`);
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v === undefined) continue;
        url.searchParams.set(k, String(v));
      }
    }
    return url.toString();
  }

  /**
   * Combine an external signal with a timeout into one. We use a plain
   * AbortController rather than `AbortSignal.any` / `AbortSignal.timeout`
   * because Node 24's undici fetch sometimes rejects composed signals from
   * those APIs (`Expected signal to be an instance of AbortSignal`).
   */
  private combineSignal(external: AbortSignal | undefined, timeoutMs: number): AbortSignal {
    const controller = new AbortController();
    const onAbort = (): void => {
      controller.abort();
    };
    const timer = setTimeout(() => {
      controller.abort();
    }, timeoutMs);
    controller.signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
      },
      { once: true },
    );
    if (external) {
      if (external.aborted) controller.abort();
      else external.addEventListener('abort', onAbort, { once: true });
    }
    return controller.signal;
  }

  /**
   * The Verofy enterprise API wraps every response as
   * `{ success, status, message, data }` (the payfaclite-php-sdk's
   * EnvelopeDecoder unwraps the same shape). We unwrap to the inner `data` so
   * each feature's Zod schema validates the payload directly.
   *
   * Tolerant + idempotent: a raw object that is NOT enveloped (e.g. an MSW mock
   * returning `{ insights, anomalies, models }` directly) passes through
   * unchanged, so dev mocks and the real backend both work.
   */
  private unwrapEnvelope(json: unknown): unknown {
    if (
      json !== null &&
      typeof json === 'object' &&
      'success' in json &&
      'data' in json &&
      typeof (json as { success: unknown }).success === 'boolean'
    ) {
      return (json as { data: unknown }).data;
    }
    return json;
  }

  private async safeJson(res: Response): Promise<unknown> {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
}

// Both clients target the Verofy Connect backend now: the Management client directly, and
// the AI (/v2/*) client through Connect's proxy. So they share ONE base — VITE_CONNECT_AUTH_BASE
// (relative in dev, e.g. /connect-api proxied by Vite; absolute in prod/staging). ApiClient's
// `new URL(path, base)` needs an ABSOLUTE base, so a relative value is resolved against the origin.
const connectBase = /^https?:\/\//.test(env.VITE_CONNECT_AUTH_BASE)
  ? env.VITE_CONNECT_AUTH_BASE
  : `${window.location.origin}${env.VITE_CONNECT_AUTH_BASE}`;

// AI Intelligence + MCP (/v2/*) — reaches the main service through the Connect proxy.
export const apiClient = new ApiClient({ baseUrl: connectBase });
// Management (users/companies/customers/products/estate/keys) — the Connect backend directly.
export const connectClient = new ApiClient({ baseUrl: connectBase });
